import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { moduleEvaluationsAPI } from '../../services/api';
import './ModuleEvaluations.css';

const SCORE_OPTIONS = [
  { value: 'helemaal_oneens', label: 'Helemaal oneens' },
  { value: 'oneens', label: 'Oneens' },
  { value: 'eens', label: 'Eens' },
  { value: 'helemaal_eens', label: 'Helemaal eens' },
  { value: 'niet_van_toepassing', label: 'Niet van toepassing' }
];

const StudentModuleEvaluations = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [okMessage, setOkMessage] = useState('');
  const [activeEvaluationKey, setActiveEvaluationKey] = useState('');
  const [answers, setAnswers] = useState({});
  const [overallComment, setOverallComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const activeEvaluation = useMemo(() => {
    if (!activeEvaluationKey) return null;
    return pending.find((item) => String(item.evaluationKey || `${item.templateId}-${item.module?._id || item.module?.id || item.moduleId}`) === String(activeEvaluationKey)) || null;
  }, [activeEvaluationKey, pending]);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await moduleEvaluationsAPI.getStudentPending();
      const items = response.evaluations || [];
      setPending(items);
      if (!activeEvaluationKey && items.length > 0) {
        const firstKey = String(items[0].evaluationKey || `${items[0].templateId}-${items[0].module?._id || items[0].module?.id || items[0].moduleId}`);
        setActiveEvaluationKey(firstKey);
      }
    } catch (loadError) {
      setError(loadError.message || 'Evaluaties laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [activeEvaluationKey]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    setAnswers({});
    setOverallComment('');
  }, [activeEvaluationKey]);

  const setQuestionScore = (segmentKey, questionId, score) => {
    setAnswers((prev) => ({
      ...prev,
      [segmentKey]: {
        ...(prev[segmentKey] || {}),
        [questionId]: score
      }
    }));
  };

  const submitEvaluation = async (event) => {
    event.preventDefault();
    if (!activeEvaluation) return;

    const payloadAnswers = (activeEvaluation.segments || [])
      .map((segment) => {
        const segmentAnswerMap = answers[segment.key] || {};
        const segmentAnswers = (segment.questions || [])
          .filter((question) => question.isActive !== false)
          .map((question) => ({
            questionId: question._id || question.id,
            score: segmentAnswerMap[String(question._id || question.id)]
          }))
          .filter((answer) => Boolean(answer.score));

        return {
          segmentKey: segment.key,
          answers: segmentAnswers
        };
      })
      .filter((segment) => segment.answers.length > 0);

    if (payloadAnswers.length === 0) {
      setError('Beantwoord minimaal 1 vraag voordat je indient.');
      return;
    }

    setSubmitting(true);
    setError('');
    setOkMessage('');
    try {
      await moduleEvaluationsAPI.submitStudentResponse({
        templateId: activeEvaluation.templateId,
        moduleId: activeEvaluation.module?._id || activeEvaluation.module?.id || activeEvaluation.moduleId,
        answers: payloadAnswers,
        overallComment
      });
      setOkMessage('Dank je! Je evaluatie is opgeslagen.');
      await loadPending();
      setAnswers({});
      setOverallComment('');
    } catch (submitError) {
      setError(submitError.message || 'Opslaan van evaluatie mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="student-module-evaluations-page">
      <div className="page-header">
        <h1>Module Evaluaties</h1>
        <p className="page-subtitle">Vul je evaluatie in na afronding van een module.</p>
      </div>

      {error && <p className="message error">{error}</p>}
      {okMessage && <p className="message ok">{okMessage}</p>}

      {loading ? (
        <p>Laden...</p>
      ) : pending.length === 0 ? (
        <div className="empty-state">
          <p>Je hebt momenteel geen open evaluatieformulieren.</p>
        </div>
      ) : (
        <div className="evaluation-layout">
          <aside className="evaluation-list">
            <h2>Open evaluaties</h2>
            {pending.map((item) => {
              const itemKey = String(item.evaluationKey || `${item.templateId}-${item.module?._id || item.module?.id || item.moduleId}`);
              return (
              <button
                key={itemKey}
                type="button"
                className={`evaluation-list-item ${itemKey === String(activeEvaluationKey) ? 'active' : ''}`}
                onClick={() => setActiveEvaluationKey(itemKey)}
              >
                <strong>{item.templateName}</strong>
                <span>{item.module?.name || item.module?.code || '-'}</span>
                <small>{item.course?.code ? `${item.course.code} - ${item.course.name}` : (item.course?.name || '')}</small>
              </button>
              );
            })}
          </aside>

          <section className="evaluation-form-wrap">
            {activeEvaluation && (
              <form className="evaluation-form" onSubmit={submitEvaluation}>
                <h2>{activeEvaluation.templateName}</h2>
                <p className="muted">Module: {activeEvaluation.module?.name || activeEvaluation.module?.code || '-'}</p>

                {(activeEvaluation.segments || []).map((segment) => (
                  <div key={segment.key} className="segment-block">
                    <h3>{segment.title}</h3>
                    {(segment.questions || []).filter((question) => question.isActive !== false).map((question) => (
                      <div key={question._id || question.id} className="question-row">
                        <p>{question.text}</p>
                        <div className="score-options">
                          {SCORE_OPTIONS.map((option) => {
                            const questionId = String(question._id || question.id);
                            return (
                              <label key={`${questionId}-${option.value}`} className="score-option">
                                <input
                                  type="radio"
                                  name={`${segment.key}-${questionId}`}
                                  value={option.value}
                                  checked={(answers[segment.key] || {})[questionId] === option.value}
                                  onChange={() => setQuestionScore(segment.key, questionId, option.value)}
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                <div className="comment-block">
                  <label>Algemene opmerking (optioneel)</label>
                  <textarea
                    className="form-input"
                    rows={4}
                    value={overallComment}
                    onChange={(e) => setOverallComment(e.target.value)}
                    placeholder="Jouw feedback..."
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? 'Verzenden...' : 'Evaluatie verzenden'}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default StudentModuleEvaluations;
